import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const ChannelsSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList } = useBuild();
  const [currentChannel, setCurrentChannel] = useState('');
  const [boost, setBoost] = useState(1);
  const [channelOptions, setChannelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch channel options on component mount
  useEffect(() => {
    const fetchChannelOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const channelsRaw = await fetchCachedList('/search/frequent_values/farcaster-items/channels');
        const channelsData = channelsRaw.map(channel => ({
          id: channel.id,
          label: channel.id.split('/').pop(),
          count: channel.count
        }));
        setChannelOptions(channelsData);
      } catch (err) {
        console.error('Failed to fetch channel options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChannelOptions();
  }, [fetchCachedList]);

  const handleChannelAction = (actionType) => {
    if (!currentChannel) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: 'channels',
      value: [currentChannel]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentChannel(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load channels: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Channel</InputLabel>
        <Select
          value={currentChannel}
          label="Channel"
          onChange={(e) => setCurrentChannel(e.target.value)}
          disabled={loading}
        >
          {channelOptions.map((channel) => (
            <MenuItem key={channel.id} value={channel.id}>
              {channel.label} ({channel.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleChannelAction('include')}
          disabled={!currentChannel || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleChannelAction('boost')}
            disabled={!currentChannel || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleChannelAction('exclude')}
          disabled={!currentChannel || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default ChannelsSection;

